import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import {
  base64ToBuffer,
  decryptCiphertextToBlob,
  decryptMetadata,
  deriveKeys,
  parseSecretFragment,
  signNonce,
} from './crypto';
import { authorizeDownload, downloadBlob, fetchChallenge } from './useData';

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

const FileSendDownloadPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { colors } = useTheme();

  const [passwordRequired, setPasswordRequired] = useState(false);
  const [password, setPassword] = useState('');
  const [filename, setFilename] = useState<string | null>(null);
  const [sizeLabel, setSizeLabel] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [downloadsRemaining, setDownloadsRemaining] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [ready, setReady] = useState(false);

  const loadChallenge = useCallback(async () => {
    if (!id) return;
    setError(null);
    setExpired(false);
    try {
      const ch = await fetchChallenge(id);
      setPasswordRequired(ch.passwordRequired);
      setExpiresAt(ch.expiresAt);
      setDownloadsRemaining(ch.downloadsRemaining);
      setReady(true);
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number; data?: { message?: string } } })?.response
        ?.status;
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (e as Error).message ||
        'Share not available';
      if (status === 410) {
        setExpired(true);
        setError('This link has expired or the download limit was reached.');
      } else if (status === 404) {
        setError('This share was not found.');
      } else {
        setError(msg);
      }
      setReady(false);
    }
  }, [id]);

  useEffect(() => {
    void loadChallenge();
  }, [loadChallenge]);

  const startDownload = async () => {
    if (!id) return;
    setBusy(true);
    setError(null);
    setProgress(0);
    try {
      const secret = parseSecretFragment(window.location.hash);
      const keys = await deriveKeys(secret);

      const ch = await fetchChallenge(id);
      const signature = await signNonce(keys.authKeyRaw, ch.nonce);
      const auth = await authorizeDownload(
        id,
        signature,
        ch.passwordRequired ? password : undefined,
      );

      const meta = await decryptMetadata(keys.metaKey, base64ToBuffer(auth.encryptedMetaBase64));
      setFilename(meta.filename);
      setSizeLabel(formatBytes(meta.size));

      setProgress(5);
      const buf = await downloadBlob(id, auth.downloadGrant);
      setProgress(40);
      const cipher = new Uint8Array(buf);
      const blob = await decryptCiphertextToBlob(cipher, keys.fileKey, meta, (p) => {
        setProgress(40 + Math.round((p.bytesDone / p.bytesTotal) * 60));
      });

      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = meta.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      setProgress(100);
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number; data?: { message?: string } } })?.response
        ?.status;
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (e as Error).message ||
        'Download failed';
      if (status === 410) {
        setExpired(true);
        setError('This link has expired or the download limit was reached.');
      } else {
        setError(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-gray-50 dark:bg-gray-950">
      <div
        className={cn(
          'w-full max-w-md rounded-xl border shadow-sm p-6 space-y-4',
          colors.surface,
          colors.border,
          colors.text,
        )}
      >
        <h1 className="text-lg font-semibold">Download secure file</h1>
        <p className={cn('text-sm', colors.textMuted)}>
          Decryption happens in your browser using the secret in the link. The server only stores
          encrypted data.
        </p>

        {expired && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">
            This link has expired.
          </div>
        )}

        {error && !expired && (
          <div className="rounded-lg border border-red-300 bg-red-50 dark:bg-red-950/40 dark:border-red-800 px-3 py-2 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        {ready && !expired && (
          <>
            {filename && (
              <p className="text-sm">
                <span className="font-medium">{filename}</span>
                {sizeLabel && (
                  <span className={cn('ml-2', colors.textMuted)}>{sizeLabel}</span>
                )}
              </p>
            )}
            {expiresAt && (
              <p className={cn('text-xs', colors.textMuted)}>
                Expires {new Date(expiresAt).toLocaleString()}
                {downloadsRemaining != null ? ` · ${downloadsRemaining} download(s) left` : ''}
              </p>
            )}
            {passwordRequired && (
              <label className="block text-sm">
                <span className={cn('font-medium', colors.textMuted)}>Password</span>
                <input
                  type="password"
                  className={cn(
                    'mt-1 w-full rounded-lg border px-3 py-2 text-sm',
                    colors.border,
                    colors.surface,
                  )}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={busy}
                />
              </label>
            )}
            <button
              type="button"
              disabled={busy || (passwordRequired && !password)}
              onClick={() => void startDownload()}
              className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white disabled:opacity-50"
            >
              {busy ? `Decrypting… ${progress}%` : 'Download & decrypt'}
            </button>
            {busy && (
              <div className="h-1.5 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-800">
                <div
                  className="h-full bg-primary transition-[width]"
                  style={{ width: `${progress}%` }}
                />
              </div>
            )}
          </>
        )}

        {!ready && !error && !expired && (
          <p className={cn('text-sm', colors.textMuted)}>Checking link…</p>
        )}
      </div>
    </div>
  );
};

export default FileSendDownloadPage;
